import { FC, MouseEvent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { useWindowWidth } from '@react-hook/window-size/throttled';
import classNames from 'classnames';

import { loggedSelector } from '@lootboy/lib/selectors';
import { NOOP } from '@lootboy/lib/utils';

import { TIcon } from '~/components/atoms/Icons/IconHoc';
import InfoIcon from '~/components/atoms/Icons/InfoIcon';
import SearchIcon from '~/components/atoms/Icons/SearchIcon';
import CardsListActionDropdown from '~/components/CardsListActionDropdown';

import { NAVIGATION_ID } from '~/config';
import useBooleanState, { TUseBooleanState } from '~/hooks/useBooleanState';
import usePrevious from '~/hooks/usePrevious';
import {
  diamondsContextFunctions,
  homeContextFunctions,
  TSlotsContextState,
} from '~/redux/slices/slotsFilters';
import {
  CARDS_PATH,
  COMICS_PATH,
  DIAMONDS_PATH,
  PACKS_PATH,
} from '~/routes/paths';

import consts from '~/sass/consts.module.scss';

import Balances from './Balances';
import ComicsInfoDialog from './ComicsInfoDialog';
import SearchAndFilters from './SearchAndFilters';

import styles from './AnimatedNavigation.module.scss';

interface IAnimatedNavigation {
  searchBooleanState: TUseBooleanState;
}

const contexMapper: { [index: string]: TSlotsContextState } = {
  [PACKS_PATH]: homeContextFunctions,
  [DIAMONDS_PATH]: diamondsContextFunctions,
};

export default function AnimatedNavigation({
  searchBooleanState,
}: IAnimatedNavigation): JSX.Element {
  const windowWidth = useWindowWidth();
  const isLoggedIn = useSelector(loggedSelector);

  const hideCardsPath = windowWidth >= Number(consts.navigation);

  const [search, { setTrue: setSearchTrue, setFalse: setSearchFalse }] =
    searchBooleanState;
  const comicDialogBooleanState = useBooleanState();
  const [, { setTrue: setComicDialogTrue }] = comicDialogBooleanState;

  const { path: comicsPath = '' } =
    useRouteMatch({
      path: COMICS_PATH,
      exact: true,
    }) || {};

  const { path: searchPath = '' } =
    useRouteMatch({
      path: Object.keys(contexMapper),
      exact: true,
    }) || {};

  const { path: cardsPath = '' } =
    useRouteMatch({
      path: CARDS_PATH,
      exact: true,
    }) || {};

  useEffect(() => {
    if (!searchPath && search) {
      setSearchFalse();
    }
  }, [search, searchPath, setSearchFalse]);

  const contexFunctions = contexMapper[searchPath];

  const secondIconMap: {
    [key in string]: {
      onClick: (e: MouseEvent) => void;
      Component: FC<TIcon>;
    };
  } = {
    [COMICS_PATH]: {
      onClick: setComicDialogTrue,
      Component: InfoIcon,
    },
    [CARDS_PATH]: {
      onClick: NOOP,
      Component: CardsListActionDropdown,
    },
  };

  Object.keys(contexMapper).forEach((key) => {
    secondIconMap[key] = {
      onClick: setSearchTrue,
      Component: SearchIcon,
    };
  });

  const activePath = searchPath || comicsPath || cardsPath;
  const previousActivePath = usePrevious(activePath); // it's used to keep show correct icon when hidding

  const componentPathAdjusted =
    hideCardsPath && activePath === CARDS_PATH ? '' : activePath;

  const { Component = 'div', onClick } =
    secondIconMap[componentPathAdjusted] ||
    secondIconMap[previousActivePath] ||
    {};

  return (
    <div
      className={classNames(
        styles.animatedNavigation,
        !activePath && styles.hide,
      )}
      id={NAVIGATION_ID}
    >
      <ComicsInfoDialog booleanState={comicDialogBooleanState} />
      {!!contexFunctions && (
        <SearchAndFilters
          searchBooleanState={searchBooleanState}
          contexFunctions={contexFunctions}
          key={searchPath}
        />
      )}
      <Component
        className={classNames(
          styles.secondIcon,
          cardsPath && styles.hideIcon,
          search && styles.searchVisible,
        )}
        onClick={onClick}
        disabled={!onClick}
      />
      <Balances
        hide={search}
        backgroundTransparent={Boolean(
          cardsPath && hideCardsPath && isLoggedIn,
        )}
      />
    </div>
  );
}
